import { AccountDetailModel, SubscriptionAppKeyDto } from 'src/models/account';
import { AccountDetailsData, AccountSubscriptionData } from './types';
import { AxiosResponse } from 'axios';
import { getMyAccountDetailsSubscriptionKey } from 'src/services/account';
import { useVigoToast } from 'src/@core/hooks/useVigoToast';

export const buildAccountDetailModel = (data: any): AccountDetailModel => {
  return {
    email: data.email,
    name: data.name,
    address: data.address,
    companyRegistrationNumber: data.companyRegistrationNumber,
    defaultCurrencyId: data.defaultCurrencyId,
    maxSalesOrderNumberLength: data.maxSalesOrderNumberLength,
    minSalesOrderNumberLength: data.minSalesOrderNumberLength,
    phone: data.phone,
    startOfFiscalYear: data.startOfFiscalYear,
    vatnumber: data.vatnumber
  };
};

export const buildSubscriptionsData = (res: AccountDetailsData) => {
  const subscriptionsArray: AccountSubscriptionData[] = [];
  for (let index = 0; index < res.subscriptions.length; index++) {
    const subscription = res.subscriptions[index];
    const subscriptionProduct = res.products.find(x => x.id === res.subscriptions[index].productId);
    const userScope = res.productLicenceScopes.find(x => x.isUser);
    const quantityType = res.productLicenceTypes.find(x => x.isQuantity);

    // Get allocated licence count
    let allocated: number | undefined = undefined;
    if (
      subscriptionProduct?.productLicenceScopeId === userScope?.id &&
      subscriptionProduct?.productLicenceTypeId === quantityType?.id
    ) {
      if (subscriptionProduct?.isMobileApp) {
        allocated = res.allocatedLicencesInfo?.deviceAppRegistrations.filter(
          x => x.productId === subscription.productId
        ).length;
      } else {
        allocated = res.allocatedLicencesInfo?.users;
      }
    }

    subscriptionsArray.push({
      ...subscriptionProduct,
      ...subscription,
      subscriptionId: res.subscriptions[index].id ?? '',
      accountId: res.subscriptions[index].accountId ?? '',
      quantity: res.subscriptions[index].quantity ?? undefined,
      allocated: allocated,
      thirdPartyApiUserName: res.subscriptions[index].thirdPartyApiUsername ?? undefined,
      thirdPartyApiPassword: res.subscriptions[index].thirdPartyApiSecret ?? undefined,
      isUserQuantity:
        subscriptionProduct?.productLicenceScopeId === userScope?.id &&
        subscriptionProduct?.productLicenceTypeId === quantityType?.id
    });
  }

  return subscriptionsArray.sort(x => x.displayOrder ?? 0);
};

export const ColourOptions = [
  '#2B2D42',
  '#009FE3',
  '#626679',
  '#21D0B2',
  '#FF9900',
  '#FF0066',
  '#C04CFD',
  '#FF6B00',
  '#FFFFFF',
  '#000000'
];

export const DefaultThemeColors = {
  iconFontColour: '#FFFFFF',
  navBarColour: '#2B2D42',
  tabSelectionColour: '#21D0B2',
  tabSelectionIconFontColour: '#FFFFFF'
} as const;

export const isValidHexColor = (color: string): boolean => {
  const hexColorPattern = /^#([0-9A-F]{3}|[0-9A-F]{6})$/i;

  return hexColorPattern.test(color);
};
export const isValidRGBColor = (color: string): boolean => {
  const rgbColorPattern = /^rgb\(\s*(\d{1,3}\s*,\s*){2}\d{1,3}\s*\)$/;

  return rgbColorPattern.test(color);
};
export const isValidRGBAColor = (color: string): boolean => {
  const rgbaColorPattern = /^rgba\(\s*(\d{1,3}\s*,\s*){3}(\d(\.\d+)?|1(\.0+)?)\s*\)$/;

  return rgbaColorPattern.test(color);
};
export const isValidColor = (color: string): boolean => {
  return isValidHexColor(color) || isValidRGBColor(color) || isValidRGBAColor(color);
};

export const clickCopyApiKeyHandler =
  (
    subscription: AccountSubscriptionData,
    vigoToast: ReturnType<typeof useVigoToast>['vigoToast']
  ): (() => Promise<void>) =>
  async (): Promise<void> => {
    let apiResponse: AxiosResponse<SubscriptionAppKeyDto, any>;
    try {
      apiResponse = await getMyAccountDetailsSubscriptionKey(subscription.subscriptionId);
      navigator.clipboard.writeText(apiResponse.data.key ?? '************');

      vigoToast.success('API Key copied!');
    } catch (e) {
      vigoToast.error('Error copying API Key.');
    }
  };
